import { Module, ActionContext } from "vuex";
import { RootState, AuthState } from "@/types/store";
import {
  login,
  register,
  LoginParams,
  getUserInfo,
  sendVerificationCode,
  logoutPost,
  guestLongin,
  switchAccountLogin,
} from "@/api/auth";
import {
  setToken,
  removeToken,
  setUserInfo,
  removeUserInfo,
  removePddActivityData,
} from "@/utils/auth";
import { showError } from "@/utils/toast";
import websocketService from "@/utils/websocket";
import { GoogleTag } from "@/utils/auth";
import { getCachedVisitorId, getFingerprintPayload } from "@/utils/fingerprint";

interface LoginResponse {
  token: string;
  refresh_token: string;
  is_new: boolean;
  user?: Record<string, any>;
}

type AuthActionContext = ActionContext<AuthState, RootState>;

const auth: Module<AuthState, RootState> = {
  namespaced: true,

  state: (): AuthState => {
    // Initialize state from localStorage if available
    const token = localStorage.getItem("token");
    const userInfo = localStorage.getItem("userInfo");
    const user = userInfo ? JSON.parse(userInfo) : null;
    const localUserList = localStorage.getItem("localUserList");
    const userList = localUserList ? JSON.parse(localUserList) : [];

    return {
      token,
      user: user?.user || null,
      userWallet: user?.userWallet || null,
      unreadNum: user?.unreadNum || false,
      isForbiddenCountry: false,
      registrationBonus: null,
      localUserList: userList,
      today_reward: 0,
    };
  },

  mutations: {
    SET_TOKEN(state: AuthState, token: string) {
      state.token = token;
      setToken(token);
    },
    SET_FORBIDDENCOUNTRY(state: AuthState, isForbiddenCountry: Boolean) {
      state.isForbiddenCountry = isForbiddenCountry;
    },
    SET_USER_DATA(state: AuthState, data: Record<string, any>) {
      state.user = data?.user || null;
      state.userWallet = data?.userWallet || null;
      state.unreadNum = data?.unreadNum || false;
      state.today_reward = data?.today_reward || 0;
      // Save user data to localStorage
      if (data) {
        setUserInfo(data);
      }
    },
    SET_UNREAD_NUM(state: AuthState, num: number) {
      state.unreadNum = num;
    },
    SET_REGISTRATION_BONUS(state, data) {
      state.registrationBonus = data;
    },
    CLEAR_AUTH(state: AuthState) {
      state.token = null;
      state.user = null;
      state.userWallet = null;
      state.userWallet = null;
      state.registrationBonus = null;
      removeToken();
      removeUserInfo();
      removePddActivityData();
      websocketService.disconnect();
    },
    UPDATE_USER_WALLET_BALANCE(state: AuthState, newBalance: number) {
      if (state.userWallet.total_balance !== newBalance) {
        state.userWallet.total_balance = newBalance;
      }
    },
    // 添加用户到本地用户列表
    ADD_TO_LOCAL_USER_LIST(state: AuthState, userData: any) {
      if (!userData?.user) return;

      const existingIndex = state.localUserList.findIndex(
        (item) => item.user?.id === userData.user.id
      );

      const userItem = {
        user: userData.user,
        userWallet: userData.userWallet,
        loginTime: new Date().toISOString(),
        account_name:
          userData.user.account_name ||
          userData.user.phone ||
          userData.user.username,
      };

      if (existingIndex >= 0) {
        // 更新现有用户信息
        state.localUserList[existingIndex] = userItem;
      } else {
        // 添加新用户到列表开头
        state.localUserList.unshift(userItem);
        // 限制列表最多保存10个用户
        if (state.localUserList.length > 10) {
          state.localUserList = state.localUserList.slice(0, 10);
        }
      }

      // 保存到localStorage
      localStorage.setItem(
        "localUserList",
        JSON.stringify(state.localUserList)
      );
    },
    // 清除本地用户列表
    CLEAR_LOCAL_USER_LIST(state: AuthState) {
      state.localUserList = [];
      localStorage.removeItem("localUserList");
    },
    // 从本地用户列表中移除指定用户
    REMOVE_FROM_LOCAL_USER_LIST(state: AuthState, userId: number) {
      state.localUserList = state.localUserList.filter(
        (item) => item.user?.uuid !== userId
      );
      localStorage.setItem(
        "localUserList",
        JSON.stringify(state.localUserList)
      );
    },
  },

  actions: {
    // 发送验证码
    async sendVerificationCode(
      { }: AuthActionContext,
      params: { phone: string; type: string; md5: string }
    ) {
      try {
        const res = await sendVerificationCode(
          params.type,
          params.phone,
          params.md5
        );
        if (res) {
          return res;
        }
      } catch (error) {
        console.error("Send verification code failed:", error);
        throw error;
      }
    },

    // 登录
    async login({ commit, dispatch, state }: AuthActionContext, params: LoginParams) {
      try {
        let res: any = null;
        if (params.type === "guest") {
          res = await guestLongin(
            params.visitorId,
            params.source,
            params.promoteUrlId,
            params.requestId
          );
          if (res.is_new) {
            await localStorage.setItem("isNew", res.is_new.toString());
          }
        } else {
          res = await login(params);
        }
        if (res) {
          const { token, refresh_token, is_new, ...userData } =
            res as LoginResponse;
          // 保存 token
          if (params.type === "guest") {
            await GoogleTag("101", {
              is_new: res.is_new.toString() === "true" || !!is_new,
            });
          }
          commit("SET_TOKEN", token);
          localStorage.setItem("refresh_token", refresh_token);
          commit("SET_REGISTRATION_BONUS", userData);
          // 获取完整的用户信息
          await dispatch("fetchUserInfo");
          return true;
        }
        return false;
      } catch (error) {
        console.error("Login failed:", error);
        showError(error as string);
        return false;
      }
    },
    async userRegister(
      { commit, dispatch }: AuthActionContext,
      params: LoginParams
    ) {
      try {
        const res = await register(params);
        if (res) {
          const { token, refresh_token, is_new, ...userData } =
            res as LoginResponse;
          // 保存 token
          commit("SET_REGISTRATION_BONUS", userData);
          commit("SET_TOKEN", token);
          localStorage.setItem("refresh_token", refresh_token);
          localStorage.setItem("isNew", is_new.toString());
          // 获取完整的用户信息
          await dispatch("fetchUserInfo");
          await GoogleTag("102");
          if(sessionStorage.getItem('channel_id') === "20"){
            window.dataLayer?.push({event: "register",phone: res.user.phone,
            });
          }

          return true;
        }
        return false;
      } catch (error) {
        console.error("Login failed:", error);
        showError(error as string);
        return false;
      }
    },

    // 获取用户信息
    async fetchUserInfo({ commit, state }) {
      // 如果没有 token，不获取用户信息
      if (!localStorage.getItem("token")) {
        return null;
      }
      try {
        const response = await getUserInfo();
        if(response.user.pwa_channel_id && !localStorage.getItem("isEvent")){
          GoogleTag("100", {
            promote_url_id: response.user.pwa_channel_id,
            event_id:response.user.pwa_channel_event_id,
          });
          localStorage.setItem("isEvent", "true");
        }
        if (response) {
          // 确保用户信息包含必要的字段
          const userData = {
            ...response,
            user: response.user,
            userWallet: response.user_wallet,
            unreadNum: response.unread_num || response.has_unread_notify,
            today_reward:
              response.today_reward ??
              response.user?.today_reward ??
              0,
          };
          commit("SET_USER_DATA", userData);
          // 添加用户到本地用户列表
          commit("ADD_TO_LOCAL_USER_LIST", userData);
          sessionStorage.setItem("promote_url_id", response.user.pwa_channel_id || "");
          return userData;
        }
        return null;
      } catch (error) {
        console.error("Failed to fetch user info:", error);
        throw error;
      }
    },

    // 退出登录
    async logout({ commit }, type) {
      if (type) {
        commit("CLEAR_AUTH");
      } else {
        try {
          await logoutPost();
        } catch (error) {
          console.error("Logout failed:", error);
        } finally {
          commit("CLEAR_AUTH");
        }
      }
    },
    // 退出登录
    async setForbiddenCountry({ commit }, isForbiddenCountry) {
      commit("SET_FORBIDDENCOUNTRY", isForbiddenCountry);
    },
    // 添加清理方法
    clearAuthData({ commit }) {
      commit("CLEAR_AUTH");
    },

    // 切换到指定用户账号
    async switchToUser({ commit, dispatch }, user) {
      // 获取当前访客ID
      let visitorId = getCachedVisitorId();
      if (!visitorId) {
        const fp = await getFingerprintPayload();
        visitorId = fp?.visitor_id || "";
      }
      websocketService.disconnect();
      removePddActivityData();
      // 调用切换账号登录接口
      const response = await switchAccountLogin({
        visitor_id: visitorId,
        user_id: user.id,
      });
      console.log(3444444444444444444444444444444);
      const { token, refresh_token, is_new, ...userData } =
        response as LoginResponse;
      // 保存 token
      commit("SET_REGISTRATION_BONUS", userData);
      commit("SET_TOKEN", token);
      localStorage.setItem("refresh_token", refresh_token);
      localStorage.setItem("isNew", is_new.toString());

      // 获取完整的用户信息
      await dispatch("fetchUserInfo");

      return true;
    },

    // 清除本地用户列表
    clearLocalUserList({ commit }) {
      commit("CLEAR_LOCAL_USER_LIST");
    },

    // 从本地用户列表中移除指定用户
    removeFromLocalUserList({ commit }, userId) {
      commit("REMOVE_FROM_LOCAL_USER_LIST", userId);
    },
  },

  getters: {
    isLoggedIn: (state: AuthState) => !!state.token && !!state.user,
    userInfo: (state: AuthState) => state.user,
    userWallet: (state: AuthState) => state.userWallet,
    unreadNum: (state: AuthState) => state.unreadNum,
    isForbiddenCountry: (state: AuthState) => state.isForbiddenCountry,
    localUserList: (state: AuthState) => state.localUserList,
    // 获取最近登录的用户列表（按登录时间排序）
    recentUsers: (state: AuthState) => {
      return state.localUserList
        .slice()
        .sort(
          (a, b) =>
            new Date(b.loginTime).getTime() - new Date(a.loginTime).getTime()
        );
    },
  },
};

export default auth;
